import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ActivityDay } from '@/api/types';

const LEVEL_BG = [
  'var(--bg-surface)',
  'color-mix(in srgb, var(--brand-primary) 30%, transparent)',
  'color-mix(in srgb, var(--brand-primary) 50%, transparent)',
  'color-mix(in srgb, var(--brand-primary) 75%, transparent)',
  'var(--brand-primary)',
];

function levelOf(count: number): number {
  if (count === 0) return 0;
  if (count <= 1) return 1;
  if (count <= 2) return 2;
  if (count <= 4) return 3;
  return 4;
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

interface Cell {
  date: string;
  count: number;
}

/**
 * So'nggi 365 kunlik faollik.
 *
 * Backend faqat faollik bo'lgan kunlarni qaytaradi (`activity`), qolgan kunlar
 * shu yerda 0 bilan to'ldiriladi — to'r uzluksiz bo'lishi uchun.
 */
export function Heatmap({ activity }: { activity: ActivityDay[] }) {
  const { t } = useTranslation();

  const cells = useMemo<Cell[]>(() => {
    const counts = new Map(activity.map((a) => [a.date, a.submissions]));
    const out: Cell[] = [];
    const today = new Date();

    for (let i = 364; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const iso = toIsoDate(d);
      out.push({ date: iso, count: counts.get(iso) ?? 0 });
    }
    return out;
  }, [activity]);

  const weeks: Cell[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  const totalSubmissions = cells.reduce((s, c) => s + c.count, 0);
  const activeDays = cells.reduce((s, c) => s + (c.count > 0 ? 1 : 0), 0);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm text-content-secondary">
          {t('profile.activitySubmissions', { count: totalSubmissions })}
        </span>
        <span className="text-xs text-content-muted">{t('profile.activeDays', { count: activeDays })}</span>
      </div>

      <div className="overflow-x-auto pb-2 no-scrollbar">
        <div className="flex gap-[3px]">
          {weeks.map((week, wi) => (
            <div key={wi} className="flex flex-col gap-[3px]">
              {week.map((cell) => (
                <div
                  key={cell.date}
                  title={`${cell.date}: ${cell.count}`}
                  className="h-[11px] w-[11px] rounded-[2px]"
                  style={{ background: LEVEL_BG[levelOf(cell.count)] }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-end gap-1.5 text-xs text-content-muted">
        <span>{t('profile.less')}</span>
        {LEVEL_BG.map((bg, i) => (
          <div key={i} className="h-[11px] w-[11px] rounded-[2px]" style={{ background: bg }} />
        ))}
        <span>{t('profile.more')}</span>
      </div>
    </div>
  );
}
